import { createApp } from 'vue';
import { createPinia } from 'pinia';
// Imperatively-called Element Plus services (ElMessageBox / ElMessage /
// ElNotification / ElLoading) are NOT picked up by the unplugin-resolver —
// only template `<el-...>` tags are. Without these explicit CSS imports the
// confirm dialog renders without size/padding (broken layout on logout etc.).
import 'element-plus/theme-chalk/el-overlay.css';
import 'element-plus/theme-chalk/el-message-box.css';
import 'element-plus/theme-chalk/el-message.css';
import 'element-plus/theme-chalk/el-notification.css';
import 'element-plus/theme-chalk/el-loading.css';
import './styles/tailwind.css';
// Global mobile/responsive overrides — all rules gated to ≤768px so desktop
// is untouched. Imported after tailwind so its @media rules win the cascade.
import './styles/mobile.css';

import App from './App.vue';
import { router } from './router';
import { i18n } from './i18n';

// Element Plus components & API auto-imported via unplugin-vue-components
// + unplugin-auto-import (see vite.config.ts).
//
// Template usage is fully handled by the resolver. The imperative services
// above need explicit CSS imports — keep this list in sync if you start
// using more imperative APIs.

// Stale-deploy recovery: after a new deploy, a lazy chunk's hash changes and the
// old one 404s. A tab on the old index.html then white-screens when it tries to
// import a now-missing chunk. Vite fires `vite:preloadError` on that failure —
// reload ONCE to pull the fresh index + chunks. Vue Router can hit the same
// failure during its initial lazy-route import, before app.mount(), so register
// both hooks BEFORE installing the router.
const CHUNK_RELOAD_KEY = 'castvo.chunkReloaded';

function hasReloadedForChunkFailure() {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

function markReloadedForChunkFailure() {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch {
    /* ignore */
  }
}

function clearChunkFailureReload() {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

function isChunkLoadFailure(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|Expected a JavaScript-or-Wasm module script|Unable to preload CSS/i.test(message);
}

function recoverFromChunkLoadFailure(error?: unknown) {
  if (error != null && !isChunkLoadFailure(error)) return;
  if (hasReloadedForChunkFailure()) return;
  markReloadedForChunkFailure();
  window.location.reload();
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  recoverFromChunkLoadFailure();
});
router.onError(recoverFromChunkLoadFailure);
router.afterEach(clearChunkFailureReload);

const app = createApp(App);
app.use(createPinia());
app.use(router);
app.use(i18n);
app.mount('#app');

// 关闭旧 PWA 缓存：之前的 Service Worker 会把 HTML 壳层和 hash 分包预缓存，
// 频繁部署后可能把 /assets/*.js 错误地命中为 text/html，导致模块 MIME 报错。
// 新版本不再注册 SW；这里负责清掉已安装的旧 SW 和 caches，清理后只刷新一次。
if ('serviceWorker' in navigator) {
  const SW_CLEANUP_RELOAD_KEY = 'castvo.swCleanupReloaded';
  const hadController = Boolean(navigator.serviceWorker.controller);

  Promise.all([
    navigator.serviceWorker.getRegistrations().then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister())),
    ),
    'caches' in window
      ? caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      : Promise.resolve([]),
  ])
    .then(() => {
      if (!hadController) return;
      if (sessionStorage.getItem(SW_CLEANUP_RELOAD_KEY) === '1') return;
      sessionStorage.setItem(SW_CLEANUP_RELOAD_KEY, '1');
      window.location.reload();
    })
    .catch(() => {});
}
